// ===========================================
// Parent Enrolments Page
// ===========================================
// Lists all enrolled classes for the parent's students,
// with a button to request enrolment in a new class.

import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  Skeleton,
  Alert,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import {
  School as SchoolIcon,
  Add as AddIcon,
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSnackbar } from 'notistack';
import PageHeader from '../../components/common/PageHeader';
import { useMyCalendarEvents, useCalendarInstruments, useCalendarProgramLevels } from '../../hooks/useHybridBooking';
import { useMyChildren } from '../../hooks/useParentProfile';
import { getContrastTextColor } from '../../styles/brandColors';
import { useSubmitEnquiry } from '../../hooks/useEnquiries';
import { useSchoolInfo } from '../../hooks/useMeetAndGreet';
import TurnstileWidget from '../../components/public/TurnstileWidget';
import { useTurnstileGate, isSpamCheckFailed } from '../../hooks/useTurnstileGate';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/useAuth';
import { effectiveStartWeek, formatTermWeekDate } from '../../utils/dateUtils';

// ===========================================
// ENROLMENT REQUEST FORM
// ===========================================

const requestSchema = z.object({
  studentId: z.string().min(1, 'Please select a student'),
  instrument: z.string().min(1, 'Please select an instrument'),
  programLevel: z.string().optional(),
  message: z.string().max(500).optional(),
});

type RequestFormValues = z.infer<typeof requestSchema>;

interface EnrolmentRequestDialogProps {
  open: boolean;
  onClose: () => void;
  students: Array<{ id: string; firstName: string; lastName: string }>;
}

function EnrolmentRequestDialog({ open, onClose, students }: EnrolmentRequestDialogProps) {
  const { schoolSlug } = useTenant();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const submitEnquiry = useSubmitEnquiry();
  // #412: this request goes through the PUBLIC enquiry route, which needs a spam-check
  // token. A logged-in route without it is the follow-up issue; until then, the widget.
  const { data: schoolInfo, isLoading: loadingSchoolInfo } = useSchoolInfo(schoolSlug || '');
  const turnstile = useTurnstileGate(schoolInfo?.turnstileSiteKey);
  const [spamCheckFailed, setSpamCheckFailed] = useState(false);
  const { restart: restartTurnstile } = turnstile;
  useEffect(() => {
    // A new opening is a new widget: never resend the last opening's (spent) token.
    if (open) {
      restartTurnstile();
      setSpamCheckFailed(false);
    }
  }, [open, restartTurnstile]);
  const { data: instruments = [] } = useCalendarInstruments();
  const { data: programLevels = [] } = useCalendarProgramLevels();

  const { control, handleSubmit, reset, formState: { errors } } = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { studentId: '', instrument: '', programLevel: '', message: '' },
  });

  const onSubmit = (values: RequestFormValues) => {
    const student = students.find((s) => s.id === values.studentId);
    const studentName = student ? `${student.firstName} ${student.lastName}` : '';
    const levelLabel = values.programLevel
      ? programLevels.find((p: { id: string; name: string }) => p.id === values.programLevel)?.name || values.programLevel
      : '';

    submitEnquiry.mutate(
      {
        schoolSlug: schoolSlug || '',
        firstName: student?.firstName || '',
        lastName: student?.lastName || '',
        email: user?.email || '',
        childFirstName: studentName,
        instrument: values.instrument,
        subject: `Enrolment Request – ${values.instrument}${levelLabel ? ` (${levelLabel})` : ''}`,
        message:
          `Enrolment request for ${studentName}.\nInstrument: ${values.instrument}` +
          (levelLabel ? `\nProgram level: ${levelLabel}` : '') +
          (values.message ? `\n\nAdditional notes: ${values.message}` : ''),
        trialType: 'Enrolment Request',
        turnstileToken: turnstile.tokenForSubmit,
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Enrolment request submitted — the school will be in touch shortly.', {
            variant: 'success',
          });
          reset();
          onClose();
        },
        onError: (error) => {
          // A token is single use, and the server consumes it even when it then refuses.
          turnstile.reset();
          // A spam-check refusal is explained inline; anything else keeps the snackbar.
          if (isSpamCheckFailed(error)) setSpamCheckFailed(true);
          else enqueueSnackbar('Failed to submit request. Please try again.', { variant: 'error' });
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request New Enrolment</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="studentId"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.studentId}>
                <InputLabel>Student *</InputLabel>
                <Select {...field} label="Student *">
                  {students.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.firstName} {s.lastName}
                    </MenuItem>
                  ))}
                </Select>
                {errors.studentId && (
                  <Typography variant="caption" color="error">
                    {errors.studentId.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="instrument"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.instrument}>
                <InputLabel>Instrument *</InputLabel>
                <Select {...field} label="Instrument *">
                  {instruments.map((inst: { id: string; name: string; color?: string | null }) => (
                    <MenuItem key={inst.id} value={inst.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: inst.color || '#9DA5AF', flexShrink: 0 }} />
                        {inst.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.instrument && (
                  <Typography variant="caption" color="error">
                    {errors.instrument.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="programLevel"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Program Level (optional)</InputLabel>
                <Select {...field} label="Program Level (optional)">
                  <MenuItem value="">Any / Not sure</MenuItem>
                  {programLevels.map((level: { id: string; name: string; color?: string | null }) => (
                    <MenuItem key={level.id} value={level.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: level.color || '#9DA5AF', flexShrink: 0 }} />
                        {level.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Additional notes (optional)"
                multiline
                rows={3}
                fullWidth
                placeholder="e.g. preferred days, any experience, questions..."
                inputProps={{ maxLength: 500 }}
              />
            )}
          />

          {/* #412: the spam check (the enquiry route refuses a submission without a valid token). */}
          <Box sx={{ mt: 2 }}>
            <TurnstileWidget
              ref={turnstile.widgetRef}
              siteKey={schoolInfo?.turnstileSiteKey}
              action="enquiry"
              onToken={turnstile.onToken}
              onUnavailable={turnstile.onUnavailable}
            />
          </Box>
          {spamCheckFailed && (
            <Alert severity="warning" sx={{ mt: 1 }} data-testid="spam-check-failed">
              The spam check didn&apos;t load or complete, so your enquiry wasn&apos;t sent. Turn off any content
              blocker for this page and try again, or contact {'the school'} directly.
            </Alert>
          )}
          {true && turnstile.waiting && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="turnstile-waiting">
              Checking you&apos;re not a robot…
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={submitEnquiry.isPending || loadingSchoolInfo || !turnstile.ready}
          startIcon={submitEnquiry.isPending ? <CircularProgress size={16} /> : <AddIcon />}
        >
          Submit Request
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function ParentEnrolmentsPage() {
  const [requestOpen, setRequestOpen] = useState(false);

  const { data: myStudents = [], isLoading: studentsLoading } = useMyChildren();
  const { data: calendarEvents = [], isLoading: eventsLoading } = useMyCalendarEvents();

  // Derive unique enrolments from calendar events (lessonId + student)
  const enrolments = useMemo(() => {
    const seen = new Set<string>();
    const result: Array<{
      key: string;
      lessonId: string;
      lessonName: string;
      studentName: string;
      teacherName?: string;
      teacherColor?: string;
      instrumentName?: string;
      instrumentColor?: string;
      programLevelName?: string;
      programLevelColor?: string;
      lessonType?: string;
      lessonTypeColor?: string;
      /** `Starts Week 7 23/11/2026` — only when the child joins after week 1. */
      startsLabel?: string;
    }> = [];

    // The child's effective start (class start vs their own). Shown only when it is
    // after week 1; missing data from the API means no restriction, so no label.
    const startsLabelFor = (r: NonNullable<(typeof calendarEvents)[number]['resource']>) => {
      const startWeek = effectiveStartWeek(
        { startWeek: r.classStartWeek },
        { startWeek: r.enrolmentStartWeek }
      );
      return startWeek > 1 && r.termStartDate
        ? `Starts ${formatTermWeekDate(r.termStartDate, startWeek, r.lessonDayOfWeek)}`
        : undefined;
    };
    // Keys whose start-week fields have been read. Only LESSON events carry them — a
    // clinic BOOKING event (HYBRID_INDIVIDUAL) does not — and the feed is sorted by
    // start, so the first event of an enrolment can be a booking. Read the fields from
    // the first event that HAS them, whichever event created the row.
    const windowRead = new Set<string>();

    calendarEvents.forEach((event) => {
      const r = event.resource;
      if (!r?.lessonId || r.type === 'HYBRID_PLACEHOLDER') return;
      const key = `${r.lessonId}_${r.studentName || ''}`;
      const hasWindowFields = !!r.termStartDate;
      if (seen.has(key)) {
        if (hasWindowFields && !windowRead.has(key)) {
          windowRead.add(key);
          const row = result.find((e) => e.key === key);
          if (row) row.startsLabel = startsLabelFor(r);
        }
        return;
      }
      seen.add(key);
      if (hasWindowFields) windowRead.add(key);
      const startsLabel = hasWindowFields ? startsLabelFor(r) : undefined;
      result.push({
        key,
        lessonId: r.lessonId,
        lessonName: r.lessonName || event.title || 'Unnamed class',
        studentName: r.studentName || '',
        teacherName: r.teacherName,
        teacherColor: r.teacherColor,
        instrumentName: r.instrumentName,
        instrumentColor: r.instrumentColor,
        programLevelName: r.programLevelName,
        programLevelColor: r.programLevelColor,
        lessonType: r.type,
        lessonTypeColor: r.lessonTypeColor,
        startsLabel,
      });
    });

    return result.sort((a, b) => a.studentName.localeCompare(b.studentName));
  }, [calendarEvents]);

  // Group by student
  const byStudent = useMemo(() => {
    const map: Record<string, typeof enrolments> = {};
    enrolments.forEach((e) => {
      const key = e.studentName || 'Unknown';
      if (!map[key]) map[key] = [];
      map[key].push(e);
    });
    return map;
  }, [enrolments]);

  const isLoading = studentsLoading || eventsLoading;

  return (
    <Box>
      <PageHeader
        title="Enrolments"
        subtitle="Classes your students are enrolled in"
        breadcrumbs={[{ label: 'Dashboard', path: '..' }, { label: 'Enrolments' }]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRequestOpen(true)}
          >
            Request Enrolment
          </Button>
        }
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
          ))}
        </Box>
      ) : enrolments.length === 0 ? (
        <Alert severity="info" icon={<SchoolIcon />}>
          No current enrolments found. Use the button above to request enrolment in a new class.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {Object.entries(byStudent).map(([studentName, classes]) => (
            <Card key={studentName}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography variant="h6">{studentName}</Typography>
                  <Chip label={`${classes.length} class${classes.length !== 1 ? 'es' : ''}`} size="small" />
                </Box>
                <Divider sx={{ mb: 1 }} />
                <List disablePadding>
                  {classes.map((cls) => {
                    const instrBg = cls.instrumentColor || '#9DA5AF';
                    const instrText = getContrastTextColor(instrBg);
                    const levelBg = cls.programLevelColor || '#9DA5AF';
                    const levelText = getContrastTextColor(levelBg);
                    const teacherBg = cls.teacherColor || '#9DA5AF';
                    const teacherText = getContrastTextColor(teacherBg);
                    const typeBg = cls.lessonTypeColor || '#9DA5AF';
                    const typeText = getContrastTextColor(typeBg);
                    return (
                      <ListItem key={cls.key} disablePadding sx={{ py: 0.75, alignItems: 'flex-start' }}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: instrBg, width: 36, height: 36, mt: 0.5 }}>
                            <MusicNoteIcon sx={{ fontSize: 18, color: instrText }} />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={cls.lessonName}
                          secondary={
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {cls.instrumentName && (
                                <Chip
                                  label={cls.instrumentName}
                                  size="small"
                                  sx={{ bgcolor: instrBg, color: instrText, fontWeight: 500 }}
                                />
                              )}
                              {cls.programLevelName && (
                                <Chip
                                  label={cls.programLevelName}
                                  size="small"
                                  sx={{ bgcolor: levelBg, color: levelText, fontWeight: 500 }}
                                />
                              )}
                              {cls.teacherName && (
                                <Chip
                                  label={cls.teacherName}
                                  size="small"
                                  sx={{ bgcolor: teacherBg, color: teacherText, fontWeight: 500 }}
                                />
                              )}
                              {cls.lessonType && (
                                <Chip
                                  label={cls.lessonType.replace(/_/g, ' ')}
                                  size="small"
                                  sx={{ bgcolor: typeBg, color: typeText, fontWeight: 500 }}
                                />
                              )}
                              {cls.startsLabel && (
                                <Chip
                                  label={cls.startsLabel}
                                  size="small"
                                  variant="outlined"
                                  data-testid="enrolment-starts"
                                />
                              )}
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <EnrolmentRequestDialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        students={myStudents}
      />
    </Box>
  );
}
